import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Heart, Target, Users, Zap, Leaf, Handshake, Globe, Sun, Tractor, Laptop, Tv } from 'lucide-react';

const About = () => {
  const { i18n } = useTranslation();

  const content = {
    fr: {
      title: "À Propos d'EDENLIFE International",
      subtitle: "Vision • Productions • Héritage • Ministères",
      intro: "EDENLIFE International est une organisation à but non lucratif dédiée au développement durable et à l'amélioration de la qualité de vie en Haïti et dans la diaspora haïtienne.",
      missionTitle: "Notre Mission",
      mission: "Promouvoir le développement durable en Haïti à travers des projets innovants dans l'énergie renouvelable, l'agriculture moderne et l'entrepreneuriat. Nous croyons fermement en un avenir où chaque Haïtien a accès à l'énergie propre, à une alimentation saine et à des opportunités économiques durables.",
      visionTitle: "Notre Vision",
      vision: "Transformer Haïti en un modèle de développement durable dans les Caraïbes. D'ici 2030, nous visons à fournir de l'énergie solaire à des milliers de foyers, à former des agriculteurs aux techniques modernes et à créer des opportunités d'entrepreneuriat pour les jeunes haïtiens.",
      valuesTitle: "Nos Valeurs",
      values: [
        { icon: Leaf, title: "Durabilité", desc: "Projets écologiques respectueux de l'environnement et des générations futures" },
        { icon: Users, title: "Communauté", desc: "Impact social positif et amélioration collective des conditions de vie" },
        { icon: Handshake, title: "Transparence", desc: "Gestion claire, responsable et éthique de tous les fonds collectés" }
      ],
      marathonTitle: "Le Marathon de l'Avenir EDENLIFE 2026",
      marathonDesc: "Le Marathon de l'Avenir EDENLIFE 2026 est notre plus grande initiative de collecte de fonds. Cette campagne unique combine la générosité de nos supporters avec l'opportunité de gagner des prix exceptionnels qui transformeront des vies.",
      prizesTitle: "Les Prix à Gagner",
      prizes: [
        { icon: Sun, title: "Maison Autosuffisante", value: "450 000 USD", desc: "Maison complètement meublée avec système d'énergie solaire 10kW intégré, 3 chambres, 2 salles de bain, 6 électroménagers, salon et salle à manger." },
        { icon: Tractor, title: "Tracteur Agricole", value: "40 000 USD", desc: "Tracteur-Semeur diesel 4RM de 180 CV avec transmission multi-vitesses : adaptabilité supérieure et puissance élevée pour l'agriculture moderne à grande échelle et les travaux lourds." },
        { icon: Globe, title: "Grand Tricycle Cargo", value: "5 000 USD", desc: "Grand Tricycle Cargo de 350CC de puissance, toute équipée, idéal pour le transport de marchandises et démarrer votre entreprise." },
        { icon: Laptop, title: "Super Laptop HP Envy", value: "3 000 USD", desc: "Ordinateur portable HP Envy, processeur Intel i7-13700H 14 cœurs, écran tactile IPS Full HD 17,3\", 64 Go DDR4, SSD 4 To, clavier rétroéclairé, Thunderbolt 4, Wi-Fi 6E, HDMI 2.1, Windows 10." },
        { icon: Tv, title: "TV LG OLED 65\"", value: "2 000 USD", desc: "Téléviseur intelligent LG G5 OLED 4K UHD HDR de 65 pouces (OLED65G5SUB) – 2025, qualité d'image exceptionnelle." }
      ],
      impactTitle: "Votre Impact",
      impactDesc: "Chaque billet acheté contribue directement au financement de nos projets en Haïti. En participant au Marathon de l'Avenir EDENLIFE 2026, vous ne tentez pas seulement de gagner des prix extraordinaires — vous aidez aussi à construire un avenir plus durable pour des milliers de familles haïtiennes.",
      stats: [
        { value: "500 000", label: "USD Objectif" },
        { value: "5", label: "Prix Majeurs" },
        { value: "2026", label: "Année du Marathon" }
      ],
      contactTitle: "Nous Contacter",
      address: "6205, Blvd. des Grandes Prairies, Suite 215-216, Saint-Léonard, QC H1P 1A2, Canada",
      phone: "1 866-722-7007",
      email: "contact@marathon-edenlifeintl.org"
    },
    en: {
      title: "About EDENLIFE International",
      subtitle: "Vision • Productions • Heritage • Ministries",
      intro: "EDENLIFE International is a non-profit organization dedicated to sustainable development and improving quality of life in Haiti and the Haitian diaspora.",
      missionTitle: "Our Mission",
      mission: "Promote sustainable development in Haiti through innovative projects in renewable energy, modern agriculture and entrepreneurship. We firmly believe in a future where every Haitian has access to clean energy, healthy food and sustainable economic opportunities.",
      visionTitle: "Our Vision",
      vision: "Transform Haiti into a model of sustainable development in the Caribbean. By 2030, we aim to provide solar energy to thousands of households, train farmers in modern techniques and create entrepreneurship opportunities for young Haitians.",
      valuesTitle: "Our Values",
      values: [
        { icon: Leaf, title: "Sustainability", desc: "Ecological projects that respect the environment and future generations" },
        { icon: Users, title: "Community", desc: "Positive social impact and collective improvement of living conditions" },
        { icon: Handshake, title: "Transparency", desc: "Clear, responsible and ethical management of all collected funds" }
      ],
      marathonTitle: "The EDENLIFE Marathon 2026",
      marathonDesc: "The EDENLIFE Marathon 2026 is our largest fundraising initiative. This unique campaign combines the generosity of our supporters with the opportunity to win exceptional prizes that will transform lives.",
      prizesTitle: "Prizes to Win",
      prizes: [
        { icon: Sun, title: "Self-Sufficient House", value: "$450,000 USD", desc: "Fully furnished house with integrated 10kW solar energy system, 3 bedrooms, 2 bathrooms, 6 appliances, living room and dining room." },
        { icon: Tractor, title: "Agricultural Tractor", value: "$40,000 USD", desc: "180 HP 4WD diesel seeder tractor with multi-speed transmission: superior adaptability and high power for large-scale modern agriculture and heavy-duty work." },
        { icon: Globe, title: "Large Cargo Tricycle", value: "$5,000 USD", desc: "Large 350CC powered Cargo Tricycle, fully equipped, ideal for goods transport and starting your business." },
        { icon: Laptop, title: "Super HP Envy Laptop", value: "$3,000 USD", desc: "HP Envy laptop, Intel i7-13700H 14-core processor, 17.3\" Full HD IPS touchscreen, 64GB DDR4, 4TB SSD, backlit keyboard, Thunderbolt 4, Wi-Fi 6E, HDMI 2.1, Windows 10." },
        { icon: Tv, title: "LG OLED 65\" TV", value: "$2,000 USD", desc: "LG G5 65-inch 4K UHD HDR OLED Smart TV (OLED65G5SUB) – 2025, exceptional picture quality." }
      ],
      impactTitle: "Your Impact",
      impactDesc: "Every ticket purchased directly contributes to funding our projects in Haiti. By participating in the EDENLIFE Marathon 2026, you're not only trying to win extraordinary prizes — you're also helping build a more sustainable future for thousands of Haitian families.",
      stats: [
        { value: "500,000", label: "USD Goal" },
        { value: "5", label: "Major Prizes" },
        { value: "2026", label: "Marathon Year" }
      ],
      contactTitle: "Contact Us",
      address: "6205 Blvd. des Grandes Prairies, Suite 215-216, Saint-Leonard, QC H1P 1A2, Canada",
      phone: "1 866-722-7007",
      email: "contact@marathon-edenlifeintl.org"
    },
    ht: {
      title: "Sou EDENLIFE Entènasyonal",
      subtitle: "Vizyon • Pwodiksyon • Eritaj • Ministè",
      intro: "EDENLIFE Entènasyonal se yon òganizasyon san bi likratif ki dedye a devlopman dirab ak amelyorasyon kalite lavi an Ayiti ak nan dyaspora ayisyen an.",
      missionTitle: "Misyon Nou",
      mission: "Ankouraje devlopman dirab an Ayiti atravè pwojè inovatè nan enèji renouvlab, agrikilti modèn ak antreprenarya. Nou kwè fèmman nan yon avni kote chak Ayisyen gen aksè a enèji pwòp, manje sen ak opòtinite ekonomik dirab.",
      visionTitle: "Vizyon Nou",
      vision: "Transfòme Ayiti nan yon modèl devlopman dirab nan Karayib la. Avan 2030, nou vize pou bay enèji solè a dè milye kay, fòme kiltivatè nan teknik modèn epi kreye opòtinite antreprenarya pou jèn Ayisyen yo.",
      valuesTitle: "Valè Nou yo",
      values: [
        { icon: Leaf, title: "Dirabilite", desc: "Pwojè ekolojik ki respekte anviwònman an ak jenerasyon k ap vini yo" },
        { icon: Users, title: "Kominote", desc: "Enpak sosyal pozitif ak amelyorasyon kolektif kondisyon lavi" },
        { icon: Handshake, title: "Transparans", desc: "Jesyon klè, responsab ak etik tout fon kolekte yo" }
      ],
      marathonTitle: "Maraton EDENLIFE 2026",
      marathonDesc: "Maraton EDENLIFE 2026 se pi gwo inisyativ koleksyon fon nou. Kanpay inik sa a konbine jenerozite sipòtè nou yo ak opòtinite pou genyen pri eksepsyonèl ki pral transfòme lavi.",
      prizesTitle: "Pri pou Genyen",
      prizes: [
        { icon: Sun, title: "Kay Otonòm", value: "450 000 USD", desc: "Kay konplètman meble ak sistèm enèji solè 10kW entegre, 3 chanm, 2 sal de ben, 6 elektwomenaje, salon ak sal manje." },
        { icon: Tractor, title: "Traktè Agrikòl", value: "40 000 USD", desc: "Traktè-semè dyezèl 4RM 180 CV ak transmisyon plizyè vitès: adaptabilite siperyè ak gwo pisans pou agrikilti modèn nan gwo echèl ak travay lou." },
        { icon: Globe, title: "Gran Trisikl Kago", value: "5 000 USD", desc: "Gran Trisikl Kago 350CC pisans, konplètman ekipe, ideyal pou transpò machandiz ak kòmanse biznis ou." },
        { icon: Laptop, title: "Sipè Laptop HP Envy", value: "3 000 USD", desc: "Òdinatè pòtab HP Envy, pwosesè Intel i7-13700H 14 nwayo, ekran taktil IPS Full HD 17,3\", 64 Go DDR4, SSD 4 To, klavye retroeklere, Thunderbolt 4, Wi-Fi 6E, HDMI 2.1, Windows 10." },
        { icon: Tv, title: "TV LG OLED 65\"", value: "2 000 USD", desc: "Televizyon entèlijan LG G5 OLED 4K UHD HDR 65 pous (OLED65G5SUB) – 2025, kalite imaj eksepsyonèl." }
      ],
      impactTitle: "Enpak Ou",
      impactDesc: "Chak tikè ou achte kontribye dirèkteman nan finansman pwojè nou yo an Ayiti. Lè w patisipe nan Maraton EDENLIFE 2026, ou pa sèlman ap eseye genyen pri ekstraòdinè — ou ap ede tou bati yon avni pi dirab pou dè milye fanmi ayisyen.",
      stats: [
        { value: "500 000", label: "USD Objektif" },
        { value: "5", label: "Pri Majè" },
        { value: "2026", label: "Ane Maraton" }
      ],
      contactTitle: "Kontakte Nou",
      address: "6205, Blvd. des Grandes Prairies, Suite 215-216, Saint-Léonard, QC H1P 1A2, Kanada",
      phone: "1 866-722-7007",
      email: "contact@marathon-edenlifeintl.org"
    },
    es: {
      title: "Acerca de EDENLIFE International",
      subtitle: "Visión • Producciones • Herencia • Ministerios",
      intro: "EDENLIFE International es una organización sin fines de lucro dedicada al desarrollo sostenible y la mejora de la calidad de vida en Haití y la diáspora haitiana.",
      missionTitle: "Nuestra Misión",
      mission: "Promover el desarrollo sostenible en Haití a través de proyectos innovadores en energía renovable, agricultura moderna y emprendimiento. Creemos firmemente en un futuro donde cada haitiano tenga acceso a energía limpia, alimentación saludable y oportunidades económicas sostenibles.",
      visionTitle: "Nuestra Visión",
      vision: "Transformar Haití en un modelo de desarrollo sostenible en el Caribe. Para 2030, aspiramos a proporcionar energía solar a miles de hogares, capacitar agricultores en técnicas modernas y crear oportunidades de emprendimiento para jóvenes haitianos.",
      valuesTitle: "Nuestros Valores",
      values: [
        { icon: Leaf, title: "Sostenibilidad", desc: "Proyectos ecológicos que respetan el medio ambiente y las generaciones futuras" },
        { icon: Users, title: "Comunidad", desc: "Impacto social positivo y mejora colectiva de las condiciones de vida" },
        { icon: Handshake, title: "Transparencia", desc: "Gestión clara, responsable y ética de todos los fondos recaudados" }
      ],
      marathonTitle: "El Maratón EDENLIFE 2026",
      marathonDesc: "El Maratón EDENLIFE 2026 es nuestra mayor iniciativa de recaudación de fondos. Esta campaña única combina la generosidad de nuestros seguidores con la oportunidad de ganar premios excepcionales que transformarán vidas.",
      prizesTitle: "Premios a Ganar",
      prizes: [
        { icon: Sun, title: "Casa Autosuficiente", value: "$450,000 USD", desc: "Casa completamente amueblada con sistema de energía solar de 10kW integrado, 3 habitaciones, 2 baños, 6 electrodomésticos, sala y comedor." },
        { icon: Tractor, title: "Tractor Agrícola", value: "$40,000 USD", desc: "Tractor-sembrador diésel 4RM de 180 CV con transmisión multi-velocidades: adaptabilidad superior y alta potencia para agricultura moderna a gran escala y trabajos pesados." },
        { icon: Globe, title: "Gran Triciclo de Carga", value: "$5,000 USD", desc: "Gran Triciclo de Carga de 350CC de potencia, completamente equipado, ideal para transporte de mercancías e iniciar su negocio." },
        { icon: Laptop, title: "Super Laptop HP Envy", value: "$3,000 USD", desc: "Portátil HP Envy, procesador Intel i7-13700H de 14 núcleos, pantalla táctil IPS Full HD de 17.3\", 64GB DDR4, SSD de 4TB, teclado retroiluminado, Thunderbolt 4, Wi-Fi 6E, HDMI 2.1, Windows 10." },
        { icon: Tv, title: "TV LG OLED 65\"", value: "$2,000 USD", desc: "Televisor inteligente LG G5 OLED 4K UHD HDR de 65 pulgadas (OLED65G5SUB) – 2025, calidad de imagen excepcional." }
      ],
      impactTitle: "Tu Impacto",
      impactDesc: "Cada boleto comprado contribuye directamente a financiar nuestros proyectos en Haití. Al participar en el Maratón EDENLIFE 2026, no solo intentas ganar premios extraordinarios — también ayudas a construir un futuro más sostenible para miles de familias haitianas.",
      stats: [
        { value: "500,000", label: "USD Meta" },
        { value: "5", label: "Premios Mayores" },
        { value: "2026", label: "Año del Maratón" }
      ],
      contactTitle: "Contáctanos",
      address: "6205, Blvd. des Grandes Prairies, Suite 215-216, Saint-Léonard, QC H1P 1A2, Canadá",
      phone: "1 866-722-7007",
      email: "contact@marathon-edenlifeintl.org"
    },
    pt: {
      title: "Sobre a EDENLIFE International",
      subtitle: "Visão • Produções • Herança • Ministérios",
      intro: "EDENLIFE International é uma organização sem fins lucrativos dedicada ao desenvolvimento sustentável e à melhoria da qualidade de vida no Haiti e na diáspora haitiana.",
      missionTitle: "Nossa Missão",
      mission: "Promover o desenvolvimento sustentável no Haiti através de projetos inovadores em energia renovável, agricultura moderna e empreendedorismo. Acreditamos firmemente em um futuro onde cada haitiano tenha acesso a energia limpa, alimentação saudável e oportunidades econômicas sustentáveis.",
      visionTitle: "Nossa Visão",
      vision: "Transformar o Haiti em um modelo de desenvolvimento sustentável no Caribe. Até 2030, pretendemos fornecer energia solar a milhares de lares, treinar agricultores em técnicas modernas e criar oportunidades de empreendedorismo para jovens haitianos.",
      valuesTitle: "Nossos Valores",
      values: [
        { icon: Leaf, title: "Sustentabilidade", desc: "Projetos ecológicos que respeitam o meio ambiente e as gerações futuras" },
        { icon: Users, title: "Comunidade", desc: "Impacto social positivo e melhoria coletiva das condições de vida" },
        { icon: Handshake, title: "Transparência", desc: "Gestão clara, responsável e ética de todos os fundos arrecadados" }
      ],
      marathonTitle: "A Maratona EDENLIFE 2026",
      marathonDesc: "A Maratona EDENLIFE 2026 é nossa maior iniciativa de arrecadação de fundos. Esta campanha única combina a generosidade de nossos apoiadores com a oportunidade de ganhar prêmios excepcionais que transformarão vidas.",
      prizesTitle: "Prêmios a Ganhar",
      prizes: [
        { icon: Sun, title: "Casa Autossuficiente", value: "$450.000 USD", desc: "Casa totalmente mobiliada com sistema de energia solar de 10kW integrado, 3 quartos, 2 banheiros, 6 eletrodomésticos, sala de estar e sala de jantar." },
        { icon: Tractor, title: "Trator Agrícola", value: "$40.000 USD", desc: "Trator-semeador diesel 4RM de 180 CV com transmissão multi-velocidades: adaptabilidade superior e alta potência para agricultura moderna em grande escala e trabalhos pesados." },
        { icon: Globe, title: "Grande Triciclo de Carga", value: "$5.000 USD", desc: "Grande Triciclo de Carga de 350CC de potência, totalmente equipado, ideal para transporte de mercadorias e iniciar seu negócio." },
        { icon: Laptop, title: "Super Laptop HP Envy", value: "$3.000 USD", desc: "Notebook HP Envy, processador Intel i7-13700H de 14 núcleos, tela touch IPS Full HD de 17.3\", 64GB DDR4, SSD de 4TB, teclado retroiluminado, Thunderbolt 4, Wi-Fi 6E, HDMI 2.1, Windows 10." },
        { icon: Tv, title: "TV LG OLED 65\"", value: "$2.000 USD", desc: "Smart TV LG G5 OLED 4K UHD HDR de 65 polegadas (OLED65G5SUB) – 2025, qualidade de imagem excepcional." }
      ],
      impactTitle: "Seu Impacto",
      impactDesc: "Cada bilhete comprado contribui diretamente para financiar nossos projetos no Haiti. Ao participar da Maratona EDENLIFE 2026, você não está apenas tentando ganhar prêmios extraordinários — você também está ajudando a construir um futuro mais sustentável para milhares de famílias haitianas.",
      stats: [
        { value: "500.000", label: "USD Meta" },
        { value: "5", label: "Prêmios Principais" },
        { value: "2026", label: "Ano da Maratona" }
      ],
      contactTitle: "Entre em Contato",
      address: "6205, Blvd. des Grandes Prairies, Suite 215-216, Saint-Léonard, QC H1P 1A2, Canadá",
      phone: "1 866-722-7007",
      email: "contact@marathon-edenlifeintl.org"
    }
  };

  const t = content[i18n.language] || content.fr;

  return (
    <div className="min-h-screen pt-24 px-6 pb-12" style={{ background: 'linear-gradient(135deg, #F4F1DE 0%, #E8E3D3 100%)' }}>
      <div className="max-w-6xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          {/* Hero Section */}
          <div className="text-center mb-16">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.5 }}
              className="mb-6"
            >
              <img src="/logo-marathon-2026.png" alt="Marathon de l'Avenir EDENLIFE 2026" className="h-28 mx-auto rounded-xl shadow-lg" />
            </motion.div>
            <h1 className="text-4xl md:text-5xl font-bold text-primary mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              {t.title}
            </h1>
            <p className="text-lg text-accent font-semibold mb-4">{t.subtitle}</p>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto leading-relaxed">
              {t.intro}
            </p>
          </div>

          {/* Mission & Vision */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white/90 backdrop-blur-md rounded-3xl shadow-2xl p-8"
            >
              <Target className="w-12 h-12 text-secondary mb-4" />
              <h2 className="text-2xl font-bold text-primary mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                {t.missionTitle}
              </h2>
              <p className="text-gray-700 leading-relaxed">{t.mission}</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white/90 backdrop-blur-md rounded-3xl shadow-2xl p-8"
            >
              <Zap className="w-12 h-12 text-accent mb-4" />
              <h2 className="text-2xl font-bold text-primary mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                {t.visionTitle}
              </h2>
              <p className="text-gray-700 leading-relaxed">{t.vision}</p>
            </motion.div>
          </div>

          {/* Our Values */}
          <div className="bg-white/90 backdrop-blur-md rounded-3xl shadow-2xl p-8 md:p-12 mb-16">
            <h2 className="text-3xl font-bold text-primary mb-8 text-center" style={{ fontFamily: 'Playfair Display, serif' }}>
              {t.valuesTitle}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {t.values.map((value, index) => {
                const Icon = value.icon;
                return (
                  <div key={index} className="text-center">
                    <div className="w-16 h-16 mx-auto rounded-full bg-primary/10 flex items-center justify-center mb-4">
                      <Icon className="w-8 h-8 text-primary" />
                    </div>
                    <h3 className="text-xl font-bold text-primary mb-2">{value.title}</h3>
                    <p className="text-gray-600">{value.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Marathon Section */}
          <div className="bg-white/90 backdrop-blur-md rounded-3xl shadow-2xl p-8 md:p-12 mb-16">
            <h2 className="text-3xl font-bold text-primary mb-6 text-center" style={{ fontFamily: 'Playfair Display, serif' }}>
              {t.marathonTitle}
            </h2>
            <p className="text-gray-700 leading-relaxed text-center max-w-4xl mx-auto mb-10">
              {t.marathonDesc}
            </p>
            
            <h3 className="text-2xl font-bold text-primary mb-6 text-center">{t.prizesTitle}</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {t.prizes.map((prize, index) => {
                const Icon = prize.icon;
                return (
                  <div key={index} className="bg-gradient-to-br from-primary/5 to-accent/5 rounded-2xl p-6 text-center border border-primary/10">
                    <Icon className="w-12 h-12 text-secondary mx-auto mb-4" />
                    <h4 className="text-xl font-bold text-primary mb-2">{prize.title}</h4>
                    <p className="text-2xl font-bold text-accent mb-3">{prize.value}</p>
                    <p className="text-sm text-gray-600">{prize.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Impact Section */}
          <div className="bg-gradient-to-br from-primary to-accent rounded-3xl p-8 md:p-12 text-white text-center mb-16">
            <Heart className="w-16 h-16 mx-auto mb-6 opacity-80" />
            <h2 className="text-3xl font-bold mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
              {t.impactTitle}
            </h2>
            <p className="text-lg leading-relaxed max-w-4xl mx-auto mb-10 text-white/90">
              {t.impactDesc}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {t.stats.map((stat, index) => (
                <div key={index}>
                  <div className="text-4xl md:text-5xl font-bold mb-2">{stat.value}</div>
                  <div className="text-white/80">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Contact Info */}
          <div className="bg-white/90 backdrop-blur-md rounded-3xl shadow-2xl p-8 text-center">
            <h2 className="text-2xl font-bold text-primary mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
              {t.contactTitle}
            </h2>
            <div className="space-y-3 text-gray-700">
              <p className="font-semibold">EDENLIFE International</p>
              <p>{t.address}</p>
              <p>📞 <a href="tel:+18667227007" className="text-primary hover:underline">{t.phone}</a></p>
              <p>✉️ <a href="mailto:contact@marathon-edenlifeintl.org" className="text-primary hover:underline">{t.email}</a></p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default About;
